import { insertHost, keepHostAttached, readInlinePosition } from '../inline-host';
import type { GuideRenderContext } from '../types';
import { waitForElement } from '../wait-for-element';
import { BaseRenderer } from './base-renderer';
import { mountCustomFrame } from './custom-frame';

/**
 * Guía `inline-custom`: HTML/CSS/JS del cliente (iframe sandbox, ver
 * `custom-frame.ts`) EMBEBIDO en el flujo del DOM, junto a un elemento ancla
 * (`step.selector` + `step.style.inlinePosition`). Como el inline estándar pero
 * con código propio en vez de bloques.
 */
export class InlineCustomRenderer extends BaseRenderer {
  async render(context: GuideRenderContext): Promise<void> {
    const step = context.guide.guideSteps[0];
    if (!step || typeof step.html !== 'string' || step.html.length === 0) return;

    const selector = step.selector ?? context.guide.activationRules.selector;
    if (typeof selector !== 'string' || selector.length === 0) return;

    const anchor = await waitForElement(selector);
    if (!anchor) return;

    const { host, root } = this.createHost();
    host.style.display = 'block';
    const position = readInlinePosition(step.style);
    insertHost(anchor, host, position);
    // El framework del host puede borrar el nodo al re-renderizar → watchdog.
    this.registerCleanup(keepHostAttached(host, selector, position));
    const doc = root.ownerDocument ?? document;

    const wrapper = doc.createElement('div');
    wrapper.className = 'veo-custom-inline';

    const { iframe, cleanup } = mountCustomFrame(doc, step, context, { width: '100%' });
    this.registerCleanup(cleanup);

    wrapper.appendChild(iframe);
    // Sin ✕ del host: las guías inline-custom cierran desde su propio HTML.
    root.appendChild(wrapper);

    context.onInteraction({ guideId: context.guide.guideId, stepIndex: 0, action: 'shown' });
  }
}
